import {create} from 'zustand'
import {Media} from '@features/photos-upload/types.ts'

export interface PhotosUploadStore {
    isOpen: boolean
    assignmentId: number | null
    step: 'idle' | 'upload' | 'uploaded'
    dropzoneFiles: File[]
    selectedDropzoneFiles: string[]
    uploadingFiles: Array<{media: File; progressValue: number; isError: boolean}>
    uploadedFiles: Media[]
    selectedUploadedFiles: number[]
    maxFiles: number
    setIsOpen: (isOpen: boolean, assignmentId?: number) => void
    setStep: (step: PhotosUploadStore['step']) => void
    addUploadingFile: (uploadingFile: File) => void
    addUploadedFile: (uploadedFile: Media) => void
    removeUploadingFile: (uploadingFile: File) => void
    addDropzoneFiles: (dropzoneFiles: File[]) => void
    selectDropzoneFiles: (dropzoneFilesNames: string[]) => void
    unselectDropzoneFiles: (dropzoneFilesNames: string[]) => void
    removeDropzoneFiles: (dropzoneFilesNames: string[]) => void
    setUploadedFiles: (uploadedFiles: Media[]) => void
    selectUploadedFiles: (uploadedFilesIds: number[]) => void
    unselectUploadedFiles: (uploadedFilesIds: number[]) => void
    removeUploadedFiles: (uploadedFilesIds: number[]) => void
    setUploadingFileIsError: (fileName: string, isError: boolean) => void
    setUploadingFileProgressValue: (fileName: string, progressValue: number) => void
    setMaxFiles: (maxFiles: number) => void
}

export const usePhotosUpload = create<PhotosUploadStore>((set, getState) => ({
    isOpen: false,
    assignmentId: null,
    step: 'idle',
    dropzoneFiles: [],
    uploadingFiles: [],
    uploadedFiles: [],
    selectedDropzoneFiles: [],
    selectedUploadedFiles: [],
    maxFiles: 0,
    setIsOpen: (isOpen, assignmentId) => {
        if (isOpen) {
            set({isOpen, assignmentId})
        } else {
            set({
                isOpen: false,
                assignmentId: null,
                step: 'idle',
                dropzoneFiles: [],
                uploadingFiles: [],
                uploadedFiles: [],
                selectedDropzoneFiles: [],
                selectedUploadedFiles: [],
                maxFiles: 0
            })
        }
    },
    setStep: step => set({step}),
    addDropzoneFiles: dropzoneFiles => {
        const prevDropzoneFiles = getState().dropzoneFiles
        const prevMaxFiles = getState().maxFiles
        set({
            dropzoneFiles: dropzoneFiles.concat(prevDropzoneFiles),
            maxFiles: prevMaxFiles - dropzoneFiles.length
        })
    },
    selectDropzoneFiles: dropzoneFilesNames => {
        const prevSelectedDropzoneFiles = getState().selectedDropzoneFiles
        set({selectedDropzoneFiles: prevSelectedDropzoneFiles.concat(dropzoneFilesNames)})
    },
    unselectDropzoneFiles: dropzoneFilesNames => {
        const prevSelectedDropzoneFiles = getState().selectedDropzoneFiles
        set({
            selectedDropzoneFiles: prevSelectedDropzoneFiles.filter(
                dropzoneFileName => !dropzoneFilesNames.includes(dropzoneFileName)
            )
        })
    },
    removeDropzoneFiles: dropzoneFilesNames => {
        const prevDropzoneFiles = getState().dropzoneFiles
        const unselectDropzoneFiles = getState().unselectDropzoneFiles
        const prevMaxFiles = getState().maxFiles
        set({
            dropzoneFiles: prevDropzoneFiles.filter(dropzoneFile => !dropzoneFilesNames.includes(dropzoneFile.name)),
            maxFiles: prevMaxFiles + dropzoneFilesNames.length
        })
        unselectDropzoneFiles(dropzoneFilesNames)
    },
    setUploadedFiles: uploadedFiles => set({uploadedFiles}),
    selectUploadedFiles: uploadedFilesIds => {
        const prevSelectedUploadedFiles = getState().selectedUploadedFiles
        set({selectedUploadedFiles: prevSelectedUploadedFiles.concat(uploadedFilesIds)})
    },
    unselectUploadedFiles: uploadedFilesIds => {
        const prevSelectedUploadedFiles = getState().selectedUploadedFiles
        set({
            selectedUploadedFiles: prevSelectedUploadedFiles.filter(
                uploadedFileId => !uploadedFilesIds.includes(uploadedFileId)
            )
        })
    },
    addUploadedFile: uploadedFile => {
        const prevUploadedFiles = getState().uploadedFiles
        set({uploadedFiles: [...prevUploadedFiles, uploadedFile]})
    },
    removeUploadedFiles: uploadedFilesIds => {
        const prevUploadedFiles = getState().uploadedFiles
        const unselectUploadedFiles = getState().unselectUploadedFiles
        const prevMaxFiles = getState().maxFiles
        set({
            uploadedFiles: prevUploadedFiles.filter(uploadedFile => !uploadedFilesIds.includes(uploadedFile.id)),
            maxFiles: prevMaxFiles + uploadedFilesIds.length
        })
        unselectUploadedFiles(uploadedFilesIds)
    },
    setUploadingFileIsError: (fileName, isError) => {
        const prevUploadingFiles = getState().uploadingFiles
        set({
            uploadingFiles: prevUploadingFiles.map(uploadingFile => {
                if (uploadingFile.media.name == fileName) {
                    return {...uploadingFile, isError}
                }
                return uploadingFile
            })
        })
    },
    setUploadingFileProgressValue: (fileName, progressValue) => {
        const prevUploadingFiles = getState().uploadingFiles
        set({
            uploadingFiles: prevUploadingFiles.map(uploadingFile => {
                if (uploadingFile.media.name == fileName) {
                    return {...uploadingFile, progressValue}
                }
                return uploadingFile
            })
        })
    },
    addUploadingFile: uploadingFile => {
        const prevUploadingFiles = getState().uploadingFiles
        set({uploadingFiles: [...prevUploadingFiles, {media: uploadingFile, progressValue: 0, isError: false}]})
    },
    removeUploadingFile: uploadingFile => {
        const prevUploadingFiles = getState().uploadingFiles
        set({uploadingFiles: prevUploadingFiles.filter(file => file.media.name != uploadingFile.name)})
    },
    setMaxFiles: maxFiles => set({maxFiles})
}))
